import {
  Browser,
  DataManager,
  DataManagerParametersType as CoreDataManagerParametersType,
  Device,
  KameleoonData,
  KameleoonDataType,
  KameleoonError,
  KameleoonLogger,
  KameleoonVisitorDataType,
  ListUtilities,
  OperatingSystem,
  Result,
  StaticData,
  TrackingStatus,
  VisitDataType,
  VisitorsDataType,
} from '@kameleoon/javascript-sdk-core/browser';
import { VISIT_DURATION } from 'src/constants';
import { IPlatformAnalyzer } from 'src/utils';

type DataManagerParametersType = CoreDataManagerParametersType & {
  platformAnalyzer: IPlatformAnalyzer;
};

export class ClientDataManager extends DataManager {
  private visitor: Visitor | null = null;
  private platformAnalyzer: IPlatformAnalyzer;

  constructor({
    dataStorage,
    infoStorage,
    cleanupInterval,
    packageInfo,
    platformAnalyzer,
  }: DataManagerParametersType) {
    KameleoonLogger.debug`CALL: new ClientDataManager(dataStorage, infoStorage, cleanupInterval: ${cleanupInterval}, packageInfo: ${packageInfo}, platformAnalyzer)`;
    super({ dataStorage, infoStorage, cleanupInterval, packageInfo });
    this.platformAnalyzer = platformAnalyzer;
    this.updateVisitor();
    KameleoonLogger.debug`RETURN: new ClientDataManager(dataStorage, infoStorage, cleanupInterval: ${cleanupInterval} packageInfo: ${packageInfo}, platformAnalyzer)`;
  }

  getUnsentData(visitorCode: string): (KameleoonDataType | StaticData)[] {
    const data = super.getUnsentData(visitorCode);
    const visitorData = super.getVisitorData(visitorCode);
    if (!visitorData) {
      return data;
    }
    const browser = visitorData[KameleoonData.Browser];
    const device = visitorData[KameleoonData.Device];
    const os = visitorData[KameleoonData.OperatingSystem];

    const staticData = new StaticData({
      browserType: browser?.browser,
      browserVersion: browser?.version,
      visitNumber: this.visitor?.visitNumber,
      timeSincePreviousVisit: this.visitor?.timeSincePreviousVisit,
      os: os?.operatingSystem,
      deviceType: device?.device,
    });
    data.push(staticData);
    return data;
  }

  public storeData(data: VisitorsDataType): Result<void, KameleoonError>;
  public storeData(
    visitorCode: string,
    ...data: KameleoonDataType[]
  ): Result<void, KameleoonError>;
  public storeData(
    firstParameter: VisitorsDataType | string,
    ...secondParameter: KameleoonDataType[]
  ): Result<void, KameleoonError> {
    if (typeof firstParameter === 'string') {
      if (secondParameter.length) {
        this.checkVisitor(firstParameter);
      }
      return super.storeData(firstParameter, ...secondParameter);
    } else {
      return super.storeData(firstParameter, true);
    }
  }

  getVisitorData(visitorCode: string): KameleoonVisitorDataType | undefined {
    this.checkVisitor(visitorCode);
    return super.getVisitorData(visitorCode);
  }

  updateLastActivity(): string | null {
    if (!this.visitor) {
      return null;
    }
    const storageData = this.dataStorage.read();

    if (!storageData.ok) {
      return null;
    }

    const data = storageData.data;
    let visitorData = data[this.visitor.visitorCode] ?? {};
    if (typeof visitorData === 'string') {
      visitorData = data[visitorData] as KameleoonVisitorDataType;
    }

    let visitsData = visitorData[KameleoonData.VisitsData];
    if (!visitsData) {
      visitsData = {
        visits: [],
        visitNumber: 0,
        type: KameleoonData.VisitsData,
        status: TrackingStatus.Sent,
        expirationTime: 0,
      };
      visitorData[KameleoonData.VisitsData] = visitsData;
    }

    const lastVisit = this.fetchVisit(
      visitsData.visits,
      visitsData.visits.length - 1,
    );

    const timeLastActivity = lastVisit?.timeLastActivity ?? 0;
    const now = Date.now();

    let visitNumber =
      visitsData.visitNumber ??
      (visitsData.visits.length ? visitsData.visits.length - 1 : 0);

    if (now - timeLastActivity > VISIT_DURATION) {
      visitNumber += timeLastActivity > 0 ? 1 : 0;
      ListUtilities.insertVisitInOrderedListMutably({
        list: visitsData.visits as VisitDataType[],
        visit: {
          timeStarted: now,
          timeLastActivity: now,
        },
      });
      this.visitor.timeSincePreviousVisit = lastVisit
        ? now - timeLastActivity
        : 0;
    } else if (visitsData.visits.length) {
      visitsData.visits[visitsData.visits.length - 1] = {
        timeStarted: lastVisit?.timeStarted ?? now,
        timeLastActivity: now,
      };
      if (this.visitor.timeSincePreviousVisit === -1) {
        const previousVisit = this.fetchVisit(
          visitsData.visits,
          visitsData.visits.length - 2,
        );
        this.visitor.timeSincePreviousVisit = previousVisit
          ? now - previousVisit.timeLastActivity
          : 0;
      }
    }
    visitsData.visitNumber = visitNumber;
    this.visitor.visitNumber = visitNumber;

    this.dataStorage.write(data);
    return this.visitor!.visitorCode;
  }

  public checkVisitor(visitorCode: string): void {
    if (!this.visitor || this.visitor.visitorCode !== visitorCode) {
      this.createVisitor(visitorCode);
    }
  }

  private createVisitor(visitorCode: string): void {
    KameleoonLogger.debug`CALL: ClientDataManager.createVisitor(visitorCode: ${visitorCode})`;
    this.visitor = new Visitor(visitorCode);

    try {
      const result = this.platformAnalyzer.getResult();
      const data = [];

      if (result.os?.name) {
        data.push(new OperatingSystem(result.os.name));
      }
      if (result.platform?.type) {
        data.push(new Device(result.platform.type));
      }
      if (result.browser?.name) {
        data.push(new Browser(result.browser.name, result.browser.version));
      }
      if (result.pageView) {
        data.push(result.pageView);
      }

      if (data.length) {
        this.storeData(visitorCode, ...data);
      }
    } finally {
      this.updateLastActivity();
    }
    KameleoonLogger.debug`RETURN: ClientDataManager.createVisitor(visitorCode: ${visitorCode})`;
  }

  private updateVisitor(): void {
    KameleoonLogger.debug`CALL: ClientDataManager.updateVisitor()`;
    const storageData = this.dataStorage.read();

    if (!storageData.ok) {
      return;
    }

    const data = storageData.data;
    let lastVisitorCode;
    let lastTimestamp = -1;

    for (const [visitorCode, visitorData] of Object.entries(data)) {
      if (typeof visitorData === 'string') {
        continue;
      }

      const visitsData = visitorData[KameleoonData.VisitsData];
      if (!visitsData) {
        if (!lastVisitorCode) {
          lastVisitorCode = visitorCode;
        }
        continue;
      }

      const lastVisit = visitsData.visits.length
        ? visitsData.visits[visitsData.visits.length - 1]
        : null;

      if (lastVisit) {
        const timeLastActivity =
          (typeof lastVisit === 'number'
            ? lastVisit
            : lastVisit?.timeLastActivity) ?? 0;
        if (timeLastActivity > lastTimestamp) {
          lastTimestamp = timeLastActivity;
          lastVisitorCode = visitorCode;
        }
      } else if (!lastVisitorCode) {
        lastVisitorCode = visitorCode;
      }
    }

    if (lastVisitorCode) {
      this.createVisitor(lastVisitorCode);
    } else {
      this.visitor = null;
      KameleoonLogger.debug`ClientDataManager no visitor found`;
    }
    KameleoonLogger.debug`RETURN: ClientDataManager.updateVisitor()`;
  }

  private fetchVisit(
    visitorVisits: (VisitDataType | number)[],
    index: number,
  ): VisitDataType | null {
    if (index < 0 || index >= visitorVisits.length) {
      return null;
    }
    const visit = visitorVisits[index];
    if (typeof visit === 'number') {
      return {
        timeStarted: visit,
        timeLastActivity: visit,
      } as VisitDataType;
    } else {
      return visit;
    }
  }
}

class Visitor {
  public visitorCode: string;
  public visitNumber: number = 0;
  public timeSincePreviousVisit: number = -1;

  constructor(visitorCode: string) {
    this.visitorCode = visitorCode;
  }
}
